import { STUDENT_SESSION_TTL } from '@/constant';
import { useMutation } from '@apollo/client';
import { Alert, Box, Dialog, DialogContent, DialogTitle } from '@mui/material';
import { useNotifications } from '@toolpad/core';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StudentBadge } from '../../components/StudentBadge';
import { CREATE_STUDENT_SESSION, type CreateStudentSessionResponse, type CreateStudentSessionVariables } from '../../gql';
import { StudentData } from '../../types/student';

const CODE_SIZE = 400;

const ErrorAlert = (props: { message: string }) => {
    return <Alert severity='error'>{props.message}</Alert>;
};

export const StudentBadgeDialog = (props: { open: boolean; onClose: () => void; data: StudentData }) => {
    const { t } = useTranslation();
    const notifications = useNotifications();
    const [createSession, { data, error }] = useMutation<CreateStudentSessionResponse, CreateStudentSessionVariables>(CREATE_STUDENT_SESSION);

    useEffect(() => {
        if (props.data.studentProfileId) {
            createSession({
                variables: {
                    studentProfileId: props.data.studentProfileId,
                    ttl: STUDENT_SESSION_TTL,
                },
            }).catch((e) => {
                notifications.show(e instanceof Error ? e.message : `${e}`, { severity: 'error' });
            });
        }
    }, [createSession, notifications, props.data.studentProfileId]);

    return (
        <Dialog open={props.open} onClose={props.onClose}>
            <DialogTitle>{t('student.actions.studentBadge')}</DialogTitle>
            <DialogContent>
                <Box sx={{ minHeight: CODE_SIZE, minWidth: CODE_SIZE, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {error ? (
                        <ErrorAlert message={error.message} />
                    ) : data?.authenticateWithClassroom.__typename === 'AuthenticateWithClassroomFailure' ? (
                        <ErrorAlert message={data.authenticateWithClassroom.message} />
                    ) : (
                        <StudentBadge
                            size={CODE_SIZE}
                            token={data?.authenticateWithClassroom.sessionToken}
                            caption={[`${props.data.name} (${props.data.studentNumber})`, `${props.data.class.name}`]}
                        />
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default StudentBadgeDialog;
